import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
export const meta = {
  title: "Hurricane Damage Statistics & Home Risk",
  description: "An above-average hurricane season puts millions of homes at risk. Discover what every homeowner needs to know about the total costs of hurricane damage.",
  category: "General",
  tags: [],
  featuredImage: {
    src: "/images/blog/AdobeStock_1190487544-scaled.webp",
    alt: "Hurricane Damage Statistics: Why 32.7 Million Homes Are at Risk"
  },
  author: {
    name: "Rebecca Henderson, Kara Credle",
    role: "Author",
    image: ""
  },
  publishedAt: "July 2, 2025",
  updatedAt: "July 3, 2025",
  showTOC: true
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    em: "em",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    hr: "hr",
    img: "img",
    li: "li",
    p: "p",
    strong: "strong",
    table: "table",
    tbody: "tbody",
    td: "td",
    th: "th",
    thead: "thead",
    tr: "tr",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: meta.title
    }), "\n", meta.description, "\n", _jsx(_components.hr, {}), "\n", _jsxs(_components.p, {
      children: ["Estimates suggest that 32.7 million homes are at risk of hurricane damage this year, with a looming combined reconstruction cost of $10.8 trillion, according to ", _jsx(_components.a, {
        href: "https://www.cotality.com/press-releases/corelogic-estimates-32-7-million-homes-at-risk-of-hurricane-damage-ahead-of-another-forecasted-active-hurricane-season#:~:text=The%20report%20found%20that%20from,reconstruction%20cost%20of%20%2410.8%20trillion",
        children: "CoreLogic"
      }), ". Only one month into 2025’s hurricane season, significant work lies ahead for homeowners living in and around areas at risk for hurricanes, especially those living in ", _jsx(_components.a, {
        href: "/home-insurance/least-resilient-coastal-cities",
        children: "coastal cities"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["As homeowners prepare their homes and families for damaging winds, torrential rain, and everything else that comes with weathering a tropical storm, the comprehensiveness of their ", _jsx(_components.a, {
        href: "/insurance/home",
        children: "homeowners’ insurance"
      }), " coverage likely sits top of mind."]
    }), "\n", _jsx(_components.p, {
      children: "We analyzed data from sources like the Insurance Information Institute, the Federal Emergency Management Agency’s (FEMA), and the National Oceanic and Atmospheric Administration’s (NOAA) to explore the costs associated with repairing and rebuilding in the aftermath of a hurricane. Our assessment of the financial toll of tropical storms also includes a review of the at-risk geographical regions, the typical hazards homeowners face, and the state of the homeowners insurance market today. We also surveyed 2,000 homeowners to explore how vulnerable some homeowners may be this hurricane season."
    }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
      children: "Key Statistics"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Since 1980, hurricanes have caused over $1.5 trillion in damage, averaging $23 billion per hurricane across the United States."
      }), "\n", _jsx(_components.li, {
        children: "In 2024, 27 billion-dollar disasters caused $182.7 billion in total damages, with 42% of that from Hurricane Helene alone, which cost $78.7 billion."
      }), "\n", _jsx(_components.li, {
        children: "More than half (53%) of residential insurance claims were denied after Hurricane Helene, while Hurricane Milton had a 39% denial rate in Florida."
      }), "\n", _jsx(_components.li, {
        children: "Only 2% of Hurricane Helene victims in North Carolina, South Carolina, and Georgia had flood insurance, highlighting a critical coverage gap for one of the most devastating hurricane hazards."
      }), "\n", _jsx(_components.li, {
        children: "Only 32% of homeowners understand what a “named storm” deductible is, according to Guardian Service’s 2025 survey of 2,000 respondents, revealing widespread confusion about hurricane insurance coverage."
      }), "\n"]
    }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
      children: "Hurricane Season Timeline and an Above Average Forecast"
    }), "\n", _jsxs(_components.p, {
      children: ["When is hurricane season, and when are hurricanes most common? According to ", _jsx(_components.a, {
        href: "https://www.nhc.noaa.gov/climo/#:~:text=The%20peak%20of%20the%20Atlantic,%2DAugust%20and%20mid%2DOctober",
        children: "NOAA’s National Hurricane Center"
      }), ":"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Hurricane season begins on June 1 and ends on November 30"
      }), "\n", _jsx(_components.li, {
        children: "September 10 is the peak of hurricane season"
      }), "\n", _jsx(_components.li, {
        children: "Hurricane activity spikes from mid-August through mid-October"
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://www.noaa.gov/news-release/noaa-predicts-above-normal-2025-atlantic-hurricane-season",
        children: "NOAA"
      }), " also predicts a 60% chance of an above-normal season this year, with a total of 13-19 named storms forecasted, 6-10 of which are expected to become hurricanes. Such speculations put up to 32.7 million homes at risk of hurricane damage, according to CoreLogic’s ", _jsx(_components.a, {
        href: "https://corelogic.foleon.com/catastrophe-risk/hurricane-2024/",
        children: "2024 Hurricane Risk Report"
      }), ". Should those homes bear the full brunt of 2025’s above-average hurricane season, combined reconstruction costs could total $10.8 trillion."]
    }), "\n", _jsx(_components.h4, {
      children: "Financial Impact of Hurricanes In the Last 40 Years"
    }), "\n", _jsxs(_components.p, {
      children: ["Hurricanes have caused more than $1.5 trillion in damages in the United States since 1980, with an average cost per hurricane of $23 million, according to the ", _jsx(_components.a, {
        href: "https://coast.noaa.gov/states/fast-facts/hurricane-costs.html",
        children: "NOAA Office for Coastal Management"
      }), ". In 2024 alone, 27 weather and climate disasters—each resulting in $1 billion or more in damages—caused a total of $182.7 billion in damages. More than two-fifths (42%) of hurricane damage in 2024 stemmed solely from Hurricane Helene, which cost $78.7 billion."]
    }), "\n", _jsx(_components.p, {
      children: "Thus far, only 2023 has seen more billion-dollar disasters (28) than 2024, with a combined total in damages of $93.1 billion. The 22 billion-dollar disasters in 2020 rank third, surpassing the record of 16 billion-dollar weather and climate disasters set in 2011 and 2017. Hurricanes Harvey, Irma, and Maria put 2017 down in the record books for the most intense hurricane season ever, with a combined $339.2 billion in damages from these three storms alone."
    }), "\n", _jsxs(_components.p, {
      children: ["Below are the top 10 costliest hurricanes in the last 40 years, per the ", _jsx(_components.a, {
        href: "https://www.iii.org/fact-statistic/facts-statistics-hurricanes",
        children: "Insurance Information Institute"
      }), ":"]
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: "Year"
          }), _jsx(_components.th, {
            children: "Hurricane"
          }), _jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Category"
            })
          }), _jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Estimated Insured Loss in Millions"
            })
          }), _jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Estimated Insured Loss in 2024 Dollars"
            })
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "2005"
          }), _jsx(_components.td, {
            children: "Katrina"
          }), _jsx(_components.td, {
            children: "5"
          }), _jsx(_components.td, {
            children: "$65,000"
          }), _jsx(_components.td, {
            children: "$104,471"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "2022"
          }), _jsx(_components.td, {
            children: "Ian"
          }), _jsx(_components.td, {
            children: "5"
          }), _jsx(_components.td, {
            children: "$54,000"
          }), _jsx(_components.td, {
            children: "$57,231"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "2021"
          }), _jsx(_components.td, {
            children: "Ida"
          }), _jsx(_components.td, {
            children: "4"
          }), _jsx(_components.td, {
            children: "$36,000"
          }), _jsx(_components.td, {
            children: "$41,540"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "2012"
          }), _jsx(_components.td, {
            children: "Sandy"
          }), _jsx(_components.td, {
            children: "3"
          }), _jsx(_components.td, {
            children: "$30,000"
          }), _jsx(_components.td, {
            children: "$40,939"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "2017"
          }), _jsx(_components.td, {
            children: "Harvey"
          }), _jsx(_components.td, {
            children: "4"
          }), _jsx(_components.td, {
            children: "$30,000"
          }), _jsx(_components.td, {
            children: "$38,571"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "2017"
          }), _jsx(_components.td, {
            children: "Irma"
          }), _jsx(_components.td, {
            children: "5"
          }), _jsx(_components.td, {
            children: "$30,050"
          }), _jsx(_components.td, {
            children: "$38,432"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "2017"
          }), _jsx(_components.td, {
            children: "Maria"
          }), _jsx(_components.td, {
            children: "5"
          }), _jsx(_components.td, {
            children: "$29,511"
          }), _jsx(_components.td, {
            children: "$37,743"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "1992"
          }), _jsx(_components.td, {
            children: "Andrew"
          }), _jsx(_components.td, {
            children: "5"
          }), _jsx(_components.td, {
            children: "$16,000"
          }), _jsx(_components.td, {
            children: "$35,845"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "2008"
          }), _jsx(_components.td, {
            children: "Ike"
          }), _jsx(_components.td, {
            children: "4"
          }), _jsx(_components.td, {
            children: "$18,200"
          }), _jsx(_components.td, {
            children: "$26,259"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "2024"
          }), _jsx(_components.td, {
            children: "Milton"
          }), _jsx(_components.td, {
            children: "5"
          }), _jsx(_components.td, {
            children: "$20,000"
          }), _jsx(_components.td, {
            children: "$20,000"
          })]
        })]
      })]
    }), "\n", _jsx(_components.p, {
      children: "*Category per the Saffir Simpson Hurricane Wind Scale"
    }), "\n", _jsx(_components.h4, {
      children: "Insurance Claim Statistics"
    }), "\n", _jsxs(_components.p, {
      children: ["Residential insurance claims after Hurricane Helene saw a 53% denial rate in Florida, followed by a 39% denial rate for claims submitted after Hurricane Milton, according to the ", _jsx(_components.a, {
        href: "https://floir.com/tools-and-data/catastrophe-reporting?utm_medium=email&utm_source=govdelivery",
        children: "Florida Office of Insurance Regulation"
      }), " (FLOIR). Denial rates for these Category 4 and 5 hurricanes, respectively, followed a trend set the year before, with nearly half of all residential insurance claims denied in 2023, according to ", _jsx(_components.a, {
        href: "https://www.insurancebusinessmag.com/us/news/property/florida-insurers-deny-nearly-half-of-hurricane-claims-ratings-agency-says-508765.aspx",
        children: "Insurance Business"
      }), "."]
    }), "\n", _jsx(_components.h3, {
      children: "Where Are Hurricanes Most Common?"
    }), "\n", _jsxs(_components.p, {
      children: ["From 1851-2022, 40% of all hurricanes that made U.S. landfall hit Florida, according to ", _jsx(_components.a, {
        href: "https://www.aoml.noaa.gov/hrd-faq/#landfalls-by-state",
        children: "NOAA’s Atlantic Oceanographic & Meteorological Laboratory"
      }), ". During that same period, 60% of all hurricanes hit either Florida or Texas. In addition to the Sunshine State, numerous states along the Gulf and Atlantic Coasts remain vulnerable to hurricanes."]
    }), "\n", _jsx(_components.h4, {
      children: "Florida: Hurricane Ground Zero"
    }), "\n", _jsxs(_components.p, {
      children: ["So, exactly how many hurricanes have hit Florida from 1851 to 2022? A total of 120 hurricanes—37 of them reaching Category 3 status or higher—have made landfall on the Florida coast in the 171-year span, according to ", _jsx(_components.a, {
        href: "https://www.aoml.noaa.gov/hrd-faq/#landfalls-by-state",
        children: "NOAA’s Atlantic Oceanographic & Meteorological Laboratory"
      }), ". That’s over a third of the total hurricanes to hit the U.S. thus far (301) and more than a third of the total major hurricanes (Category 3 or higher) to make landfall on U.S. soil (92)."]
    }), "\n", _jsxs(_components.p, {
      children: ["Florida is particularly vulnerable to both Atlantic and Gulf Coast storms, with FEMA’s ", _jsx(_components.a, {
        href: "https://hazards.fema.gov/nri/hurricane",
        children: "National Risk Index"
      }), " (NRI) identifying 34 total counties at very high or relatively high risk of hurricanes, from Duval County in the northeast, south to Monroe County, and west to Escambia County. In 2024, Hurricane Helene was the strongest hurricane on record to hit the Big Bend region, marking the third hurricane to impact that region in just over a year, according to the ", _jsx(_components.a, {
        href: "https://coast.noaa.gov/states/fast-facts/hurricane-costs.html",
        children: "NOAA Office for Coastal Management"
      }), ". The prevalence of tropical storms and major hurricanes has produced unique insurance market challenges, prompting multiple insurers to exit the state completely."]
    }), "\n", _jsx(_components.h4, {
      children: "Gulf Coast States"
    }), "\n", _jsxs(_components.p, {
      children: ["Florida, Texas, and Louisiana comprise the top three states hit by the most hurricanes in the U.S. from 1851–2022, per ", _jsx(_components.a, {
        href: "https://www.aoml.noaa.gov/hrd-faq/#landfalls-by-state",
        children: "NOAA’s Atlantic Oceanographic & Meteorological Laboratory"
      }), ". Texas’ massive coastal exposure contributed to a total of 64 hurricanes making landfall during this period, with 19 of those hurricanes reaching Category 3 or higher status. With one hurricane less than Texas, but the same number of major hurricanes making landfall, Louisiana’s vulnerability to storm surges stems from its geographical location as a major hurricane corridor."]
    }), "\n", _jsxs(_components.p, {
      children: ["FEMA’s ", _jsx(_components.a, {
        href: "https://hazards.fema.gov/nri/hurricane",
        children: "NRI"
      }), " identifies 12 Louisiana counties and nine Texas counties at very high or relatively high risk of hurricanes. While only two counties each in Mississippi (Harrison and Jackson Counties) and Alabama (Baldwin and Mobile Counties) show the same level of hurricane risk, both states receive regular severe impacts from Gulf storms during hurricane season."]
    }), "\n", _jsx(_components.h4, {
      children: "Rising Risk on the Atlantic Coast"
    }), "\n", _jsxs(_components.p, {
      children: ["Many states along the Atlantic Coast face a rising risk of hurricanes. Just last year, Hurricane Helene caused over 30 inches of historic rainfall and record-breaking flooding in western North Carolina, including Asheville, according to the ", _jsx(_components.a, {
        href: "https://coast.noaa.gov/states/fast-facts/hurricane-costs.html",
        children: "NOAA Office for Coastal Management"
      }), ". In addition, the ", _jsx(_components.a, {
        href: "https://hazards.fema.gov/nri/hurricane",
        children: "NRI"
      }), " identifies 13 counties in North Carolina that are of very high or relatively high risk of hurricanes."]
    }), "\n", _jsxs(_components.p, {
      children: ["As a wind damage corridor, South Carolina is often prone to coastal storm surges, while Georgia typically suffers from significant agricultural and property damage after a tropical storm. Helene reportedly caused at least ", _jsx(_components.a, {
        href: "https://www.agriculturedive.com/news/hurricane-helene-georgia-farm-damage-losses-pecans-poultry/729611/",
        children: "$6.46 billion agriculture"
      }), " damages in Georgia. From Virginia up the coast through Maine, hurricane frequency is lower, but these states remain vulnerable to major storms. Several counties in Connecticut, Maryland, Massachusetts, New York, and New Jersey received a very high or relatively high hurricane risk rating from the NRI."]
    }), "\n", _jsxs(_components.h3, {
      children: ["Most Common Hurricane Damage Hazards", _jsx(_components.img, {
        src: "/images/blog/AdobeStock_171085827-1-1024x683.webp",
        alt: "Property damage from hurricane wind and storm surge hazards"
      })]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.em, {
        children: "Credit: Adobe – Royalty Free"
      })
    }), "\n", _jsx(_components.p, {
      children: "Several natural phenomena combine to create the total destructive force of a hurricane. Rising water levels and hurricane-force winds rank among the most notable characteristics of these tropical natural disasters. In addition, sustained, widespread precipitation and miles-wide swaths of debris compound the most common hazards associated with hurricanes."
    }), "\n", _jsx(_components.h4, {
      children: "Wind Damage"
    }), "\n", _jsxs(_components.p, {
      children: ["Wind speeds must exceed 74 mph to reach hurricane-force levels, according to NOAA’s ", _jsx(_components.a, {
        href: "https://www.nhc.noaa.gov/prepare/hazards.php",
        children: "National Hurricane Center"
      }), ". However, even wind speeds below this threshold can cause damage to the areas surrounding the hurricane’s eye, from picking up and launching debris to spawning one or more tornadoes, per the ", _jsx(_components.a, {
        href: "https://www.weather.gov/safety/hurricane",
        children: "National Weather Service"
      }), ". The top three metropolitan areas at risk for hurricane winds in 2024 included the following, according to the ", _jsx(_components.a, {
        href: "https://www.iii.org/fact-statistic/facts-statistics-hurricanes",
        children: "Insurance Information Institute"
      }), ":"]
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Metropolitan Area"
            })
          }), _jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Number of Homes Affected"
            })
          }), _jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Reconstruction Costs"
            })
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "New York"
          }), _jsx(_components.td, {
            children: "3,765,489"
          }), _jsx(_components.td, {
            children: "$1.96 trillion"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Houston"
          }), _jsx(_components.td, {
            children: "2,092,308"
          }), _jsx(_components.td, {
            children: "$671.3 billion"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Miami"
          }), _jsx(_components.td, {
            children: "2,048,827"
          }), _jsx(_components.td, {
            children: "$511.4 billion"
          })]
        })]
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["In 2024, more than 22.7 million homes were at high risk of wind, which was projected to cost more than $7 billion in reconstruction cost value, according to the ", _jsx(_components.a, {
        href: "https://www.iii.org/fact-statistic/facts-statistics-hurricanes",
        children: "Insurance Information Institute"
      }), ". That same year, nearly 15 million homes fell within the boundaries of areas of very high risk of hurricane winds or greater, which was a projected $4 billion in reconstruction cost value. More than 6.4 million homes were considered at risk of extreme hurricane winds, which equates to $1.68 trillion in reconstruction cost values."]
    }), "\n", _jsx(_components.h4, {
      children: "Storm Surge"
    }), "\n", _jsxs(_components.p, {
      children: ["During a storm surge, water levels can exceed 20 feet above normal along the length of the coastline, according to NOAA’s ", _jsx(_components.a, {
        href: "https://www.nhc.noaa.gov/prepare/hazards.php",
        children: "National Hurricane Center"
      }), ". Rising water levels can also affect inland bodies of water, resulting in flooding in areas further from coastlines, per the ", _jsx(_components.a, {
        href: "https://www.weather.gov/safety/hurricane",
        children: "National Weather Service"
      }), ". In addition to ranking among the top three metropolitan areas at risk for storm surge in 2024, New York, Miami, and Houston also ranked among the top three metro areas for storm surge, according to the ", _jsx(_components.a, {
        href: "https://www.iii.org/fact-statistic/facts-statistics-hurricanes",
        children: "Insurance Information Institute"
      }), ":"]
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Metropolitan Area"
            })
          }), _jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Number of Homes Affected"
            })
          }), _jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Reconstruction Costs"
            })
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "New York"
          }), _jsx(_components.td, {
            children: "878,226"
          }), _jsx(_components.td, {
            children: "$415.4 billion"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Miami"
          }), _jsx(_components.td, {
            children: "509,133"
          }), _jsx(_components.td, {
            children: "$124 billion"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Houston"
          }), _jsx(_components.td, {
            children: "192,937"
          }), _jsx(_components.td, {
            children: "$52.6 billion"
          })]
        })]
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["In 2024, over 1.3 million homes were at risk of a Category 1 storm surge, corresponding to a projected reconstruction cost value of $396 billion, per the ", _jsx(_components.a, {
        href: "https://www.iii.org/fact-statistic/facts-statistics-hurricanes",
        children: "Insurance Information Institute"
      }), ". More than 4.5 million homes were at risk of Category 3 , equating to over $1.4 trillion in projected reconstruction cost value for storm surge. By comparison, Category 5 hurricanes—like Hurricanes Katrina, Milton, and Irma—put more than 7.7 million homes at risk, or $2.3 trillion in corresponding reconstruction cost value."]
    }), "\n", _jsx(_components.h4, {
      children: "Inland Flooding"
    }), "\n", _jsxs(_components.p, {
      children: ["Rising water levels due to storm surges and rainfall exceeding six inches can quickly lead to widespread flooding, including flash flooding, according to the ", _jsx(_components.a, {
        href: "https://www.nhc.noaa.gov/prepare/hazards.php",
        children: "National Hurricane Center"
      }), ". Flood conditions can persist for several days after a hurricane has subsided, until water levels return to normal or near-normal levels. Below are the top five most significant flood events since 1978 by the number of paid losses, per the ", _jsx(_components.a, {
        href: "https://www.iii.org/fact-statistic/facts-statistics-hurricanes",
        children: "Insurance Information Institute"
      }), ":"]
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Flood Event"
            })
          }), _jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Number of Paid Losses"
            })
          }), _jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Amount Paid"
            })
          }), _jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "(2024 Dollars; Billions)"
            })
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Hurricane Katrina (2005)"
          }), _jsx(_components.td, {
            children: "168,200"
          }), _jsx(_components.td, {
            children: "$26.247"
          }), _jsx(_components.td, {
            children: "$156,046"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Superstorm Sandy (2012)"
          }), _jsx(_components.td, {
            children: "132,800"
          }), _jsx(_components.td, {
            children: "$12.236"
          }), _jsx(_components.td, {
            children: "$92,139"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Hurricane Harvey (2017)"
          }), _jsx(_components.td, {
            children: "77,100"
          }), _jsx(_components.td, {
            children: "$11.591"
          }), _jsx(_components.td, {
            children: "$150,337"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Hurricane Ian (2022)"
          }), _jsx(_components.td, {
            children: "48,000"
          }), _jsx(_components.td, {
            children: "$4.555"
          }), _jsx(_components.td, {
            children: "$94,896"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Hurricane Ike (2008)"
          }), _jsx(_components.td, {
            children: "46,900"
          }), _jsx(_components.td, {
            children: "$3.911"
          }), _jsx(_components.td, {
            children: "$83,390"
          })]
        })]
      })]
    }), "\n", _jsx(_components.h4, {
      children: "Debris and Secondary Damage"
    }), "\n", _jsxs(_components.p, {
      children: ["Measuring the extent of secondary damage after a hurricane can prove challenging, if not nearly impossible. Severe wind gusts—the dying breath of hurricane-force winds and destructive tornadoes—can significantly widen the debris path, and strong currents can erode river banks across long distances via multiple swollen bodies of water. Powerful storm waves erode the coastline, causing rip currents and structural damage to structures dangerously close to the water’s edge, according to the ", _jsx(_components.a, {
        href: "https://www.weather.gov/safety/hurricane",
        children: "National Weather Service"
      }), "."]
    }), "\n", _jsx(_components.h3, {
      children: "The Insurance Coverage Crisis: What's Covered vs. What's Not"
    }), "\n", _jsxs(_components.p, {
      children: ["Although a standard homeowners insurance policy covers wind damage to the home, coverage typically falls short in the event of a hurricane. Homeowners must purchase additional hurricane and named storm deductibles to cover damage caused by a tropical storm, hurricane, tropical cyclone, or typhoon named by the National Weather Service or the National Hurricane Center, according to the ", _jsx(_components.a, {
        href: "https://content.naic.org/article/consumer-insight-what-are-named-storm-deductibles",
        children: "National Association of Insurance Commissioners"
      }), " (NAIC). These deductibles apply only if a named storm causes damage, typically once per calendar year, hurricane season, or hurricane event, depending on the policy terms."]
    }), "\n", _jsxs(_components.p, {
      children: ["Most named storm deductibles are a percentage of the home’s insured value—ranging from 1–10%—but they can also be a fixed amount. Florida, Georgia, Louisiana, North Carolina, and Texas—along with the District of Columbia and 14 other states—require hurricane or named storm deductibles, according to the NAIC. However, only 32% of the 2,000 respondents to our 2025 Guardian Service Homeowners Survey knew of “named” storm deductibles. If you’re unsure whether you have or need a named storm deductible, check out our ", _jsx(_components.a, {
        href: "/home-insurance/hurricane-season-insurance-guide",
        children: "hurricane season insurance guide"
      }), " to make sure you have adequate coverage before the first storm is forecasted."]
    }), "\n", _jsxs(_components.p, {
      children: ["In addition, a standard homeowners insurance policy does not cover damages caused by water entering your home from the outside, such as in the case of a storm surge resulting from a hurricane. Only a ", _jsx(_components.a, {
        href: "/home-insurance/flood-insurance",
        children: "flood insurance"
      }), " policy will cover repairs due to floodwaters. Only 2% of homeowners affected by Hurricane Helene had flood insurance, according to ", _jsx(_components.a, {
        href: "https://www.politico.com/news/2024/10/02/helene-flood-damage-insurance-00181952",
        children: "Politico"
      }), "."]
    }), "\n", _jsx(_components.h3, {
      children: "Protect Your Home & Lower Your Premiums"
    }), "\n", _jsxs(_components.p, {
      children: ["According to our ", _jsx(_components.a, {
        href: "/home-insurance/homeowners-insurance-preparedness-survey",
        children: "survey"
      }), ", one-third (33%) of respondents have not taken measures to protect their homes from weather damage. Preparing your home for severe climate and weather events can help minimize damage and potentially reduce the costs of rebuilding. Below are structural improvements homeowners can make to help protect their homes and even lower their deductibles, according to ", _jsx(_components.a, {
        href: "https://www.fixr.com/articles/hurricane-impact-report",
        children: "Fixr"
      }), ":"]
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Hurricane Preparation Improvement"
            })
          }), _jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Estimated Costs"
            })
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Yard work"
          }), _jsx(_components.td, {
            children: "DIY"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Proper water drainage"
          }), _jsx(_components.td, {
            children: "DIY"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Gutter cleaning"
          }), _jsx(_components.td, {
            children: "DIY"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Hurricane shutters"
          }), _jsx(_components.td, {
            children: "$95 (per window)"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Portable generator"
          }), _jsx(_components.td, {
            children: "$1,200"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Tree trimming (professional)"
          }), _jsx(_components.td, {
            children: "$450"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Roof inspection"
          }), _jsx(_components.td, {
            children: "$426"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Full roof replacement"
          }), _jsx(_components.td, {
            children: "$21,054"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Hurricane/Impact-resistant windows"
          }), _jsx(_components.td, {
            children: "$2,486 (per window)"
          })]
        })]
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["Obtaining an annual roof inspection can help homeowners assess and document the state of their roof prior to it potentially sustaining damage due to a hurricane. Investing in a garage door rated for winds exceeding 130 mph can also help protect your home’s structure during a hurricane or severe storm, according to ", _jsx(_components.a, {
        href: "https://www.realtor.com/advice/finance/hurricane-mitigation-upgrades-cut-insurance-premiums/",
        children: "Realtor.com"
      }), ". Hurricane clips or ties help reinforce a roof’s structure, while floor vents in the foundation allow water to flow through, preventing excessive pressure and resulting structural damage."]
    }), "\n", _jsxs(_components.p, {
      children: ["If you’re unsure whether your home might be eligible for a ", _jsx(_components.a, {
        href: "https://www.realtor.com/advice/finance/hurricane-mitigation-upgrades-cut-insurance-premiums/",
        children: "lower homeowners insurance premium"
      }), " after making these improvements, speak to your insurance agent. Provide any relevant receipts, photos, or certifications that demonstrate completion of these upgrades to secure available discounts. Staying on top of home and property maintenance, creating and maintaining an updated home inventory, and reviewing homeowners insurance policies and any additional coverage—ideally, before hurricane season begins—can also help homeowners prepare for severe weather."]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
